/**
 * OPVisual's program picture on the iPad: the Grid's program monitor
 * (DEC-272 follow-up, OPBridge S314 `GET /opvisual/preview.jpg`).
 *
 * OPVisual serves its output only on its own machine (`127.0.0.1:8770`);
 * OPBridge on that machine passes one JPEG through per request, scope
 * `/opvisual/state` (the thumbnail scope), bearer in the header, so it is
 * fetched as a blob, never a bare `<img src>` (that would 401).
 *
 * Polled only while a Grid with its monitor shows (`watchOpvPreview` is
 * ref-counted per machine), one request at a time, at most `FRAME_MS` apart,
 * and only while the page is visible. OPBridge reuses a frame younger than
 * 80 ms, so several devices polling together cost OPVisual ~12 fetches a
 * second at most (their figure).
 *
 * What the monitor says when there is no picture, from the answer:
 *   - 404: the bridge predates the route -> "needs an OPBridge update", and
 *     it asks again only every `NO_ROUTE_RETRY_MS`.
 *   - 502: OPVisual (or its preview server) is not answering -> "OPVisual is
 *     not running", retried every `DOWN_RETRY_MS`.
 *   - 401 / 403: this pairing lacks the scope -> said so.
 *   - anything else: "offline" and a retry.
 *
 * Plain signal over an immutable record, never a Map (a Solid read of a Map
 * tracks nothing).
 */

import { createSignal } from 'solid-js';
import { getConnection, resolveBridge } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';

/** 10 fps: OPBridge's figure for "one viewer gets a fresh frame every poll". */
export const FRAME_MS = 100;
export const DOWN_RETRY_MS = 3000;
export const NO_ROUTE_RETRY_MS = 30000;

export type OpvPreviewState = 'loading' | 'live' | 'no-route' | 'no-opvisual' | 'no-scope' | 'offline';

export interface OpvPreview {
  state: OpvPreviewState;
  /** Blob URL of the newest frame, kept while a later poll fails so the
   *  monitor does not flash black on one dropped frame. */
  url: string | null;
}

const [previews, setPreviews] = createSignal<Readonly<Record<string, OpvPreview>>>({});

/** The monitor for the machine `target` names, or null when it is not watched. */
export function opvPreview(target: string | undefined): OpvPreview | null {
  const row = target ? resolveBridge(target) : null;
  return row ? previews()[row.id] ?? null : null;
}

interface Watch {
  refs: number;
  timer: ReturnType<typeof setTimeout> | null;
  stopped: boolean;
}
const watches = new Map<string, Watch>();

function put(rowId: string, next: Partial<OpvPreview>): void {
  setPreviews((all) => {
    const cur = all[rowId] ?? { state: 'loading' as const, url: null };
    const merged = { ...cur, ...next };
    if (merged.state === cur.state && merged.url === cur.url && all[rowId]) return all;
    return { ...all, [rowId]: merged };
  });
}

/** Classify a failed fetch: what the monitor should say, and when to ask again. */
export function previewFailure(err: unknown): { state: OpvPreviewState; retryMs: number } {
  const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
  if (status === 404) return { state: 'no-route', retryMs: NO_ROUTE_RETRY_MS };
  if (status === 502) return { state: 'no-opvisual', retryMs: DOWN_RETRY_MS };
  if (status === 401 || status === 403) return { state: 'no-scope', retryMs: NO_ROUTE_RETRY_MS };
  return { state: 'offline', retryMs: DOWN_RETRY_MS };
}

async function tick(rowId: string, w: Watch): Promise<void> {
  w.timer = null;
  if (w.stopped) return;
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const conn = getConnection(rowId);
  if (hidden || !conn || conn.status() !== 'connected') {
    if (!hidden) put(rowId, { state: 'offline' });
    schedule(rowId, w, DOWN_RETRY_MS);
    return;
  }
  const started = performance.now();
  try {
    const blob = await conn.httpClient().opvisualPreview();
    if (w.stopped || watches.get(rowId) !== w) return;
    const url = URL.createObjectURL(blob);
    // Decode BEFORE swapping it in: at 10 fps a bare `src` swap leaves the
    // monitor mid-load (blank) for part of every frame. A frame that will not
    // decode is dropped and the last good one stays up.
    try {
      const im = new Image();
      im.src = url;
      await im.decode();
    } catch {
      URL.revokeObjectURL(url);
      schedule(rowId, w, DOWN_RETRY_MS);
      return;
    }
    if (w.stopped || watches.get(rowId) !== w) { URL.revokeObjectURL(url); return; }
    const old = previews()[rowId]?.url;
    put(rowId, { state: 'live', url });
    if (old) URL.revokeObjectURL(old);
    schedule(rowId, w, Math.max(0, FRAME_MS - (performance.now() - started)));
  } catch (err) {
    if (w.stopped || watches.get(rowId) !== w) return;
    const f = previewFailure(err);
    put(rowId, { state: f.state });
    schedule(rowId, w, f.retryMs);
  }
}

function schedule(rowId: string, w: Watch, ms: number): void {
  if (w.stopped) return;
  w.timer = setTimeout(() => { void tick(rowId, w); }, ms);
}

/** Watch the program picture on the machine `target` names while the
 *  returned function has not been called. Ref-counted per machine. */
export function watchOpvPreview(target: string | undefined): () => void {
  const row = target ? resolveBridge(target) : null;
  if (!row) return () => {};
  const rowId = row.id;
  let w = watches.get(rowId);
  if (!w) {
    w = { refs: 0, timer: null, stopped: false };
    watches.set(rowId, w);
    put(rowId, { state: 'loading' });
    schedule(rowId, w, 0);
  }
  w.refs += 1;
  const mine = w;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    mine.refs -= 1;
    if (mine.refs > 0) return;
    mine.stopped = true;
    if (mine.timer !== null) clearTimeout(mine.timer);
    if (watches.get(rowId) === mine) watches.delete(rowId);
    const old = previews()[rowId]?.url;
    if (old) URL.revokeObjectURL(old);
    setPreviews((all) => {
      if (!all[rowId]) return all;
      const next = { ...all };
      delete next[rowId];
      return next;
    });
  };
}
